import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  ControlButton,
  Controls,
  MiniMap,
  ReactFlowProvider,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  BookOpen,
  FileText,
  GitBranch,
  Loader2,
  Maximize2,
  Minimize2,
  PanelRightClose,
  PanelRightOpen,
  ScanSearch,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useUserDesktopConfig } from '@/OS/hooks/useUserSettings';
import { useKnowledgeGraph, type KnowledgeGraphVisualNode } from '@/hooks/useKnowledgeGraph';
import type { KnowledgeGraphEdge, KnowledgeGraphEdgeType, KnowledgeGraphNode, KnowledgeGraphNodeType } from '@/lib/librarian/types';

interface KnowledgeGraphViewProps {
  knowledgeBaseId?: string | null;
  refreshKey?: string | number;
  fullscreenHostRef?: React.RefObject<HTMLDivElement | null>;
  onOpenVaultPage?: (target: { pageId?: string | null; path?: string | null }) => void;
}

type DetailLevel = 'full' | 'compact' | 'overview';

type GraphViewportState = {
  x: number;
  y: number;
  zoom: number;
};

type RenderGraphNode = {
  id: string;
  label: string;
  metaLabel?: string;
  type: KnowledgeGraphNodeType;
  position: { x: number; y: number };
  raw: KnowledgeGraphNode | null;
  memberNodeIds: string[];
  count: number;
  isAggregate: boolean;
};

type RenderGraphEdge = KnowledgeGraphEdge & {
  count?: number;
  isAggregate?: boolean;
};

type OverviewBucket = {
  key: string;
  type: KnowledgeGraphNodeType;
  nodes: KnowledgeGraphVisualNode[];
  label?: string;
};

const nodeTypeLabels: Record<KnowledgeGraphNodeType, string> = {
  source: 'Sources',
  page: 'Pages',
  topic: 'Topics',
  entity: 'Entities',
  run: 'Runs',
};

const edgeTypeLabels: Record<KnowledgeGraphEdgeType, string> = {
  wiki_link: 'Wiki links',
  references_source: 'Page → source',
  mentions: 'Mentions',
  related_to: 'Related',
  generated_by_run: 'Run → page',
  processed_source: 'Run → source',
  derived_from_page: 'Concept → page',
  co_mentions: 'Co-mentions',
};

const nodeTypeOrder: KnowledgeGraphNodeType[] = ['source', 'page', 'topic', 'entity', 'run'];
const GRAPH_VIEWPORT_STORAGE_KEY = 'knowledge-graph-viewports';
const GRAPH_VIEWPORT_CONFIG_KEY = 'ai_knowledge_graph_viewports_v1';

function parseGraphViewportMap(value: unknown): Record<string, GraphViewportState> {
  if (!value) return {};

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return Object.entries(parsed).reduce<Record<string, GraphViewportState>>((accumulator, [knowledgeBaseId, candidate]) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      return accumulator;
    }

    const x = Number((candidate as { x?: unknown }).x);
    const y = Number((candidate as { y?: unknown }).y);
    const zoom = Number((candidate as { zoom?: unknown }).zoom);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom)) {
      return accumulator;
    }

    accumulator[knowledgeBaseId] = { x, y, zoom };
    return accumulator;
  }, {});
}

function iconForType(type: KnowledgeGraphNodeType) {
  switch (type) {
    case 'source':
      return FileText;
    case 'page':
      return BookOpen;
    case 'topic':
      return Sparkles;
    case 'entity':
      return GitBranch;
    case 'run':
      return Workflow;
  }
}

function colorForType(type: KnowledgeGraphNodeType) {
  switch (type) {
    case 'source':
      return '#0ea5e9';
    case 'page':
      return '#22c55e';
    case 'topic':
      return '#f59e0b';
    case 'entity':
      return '#8b5cf6';
    case 'run':
      return '#ef4444';
  }
}

function backgroundColorForType(type: KnowledgeGraphNodeType) {
  switch (type) {
    case 'source':
      return '#e0f2fe';
    case 'page':
      return '#dcfce7';
    case 'topic':
      return '#fef3c7';
    case 'entity':
      return '#ede9fe';
    case 'run':
      return '#fee2e2';
  }
}

function edgeColor(type: KnowledgeGraphEdgeType) {
  switch (type) {
    case 'wiki_link':
      return '#22c55e';
    case 'references_source':
      return '#0ea5e9';
    case 'mentions':
      return '#f59e0b';
    case 'related_to':
      return '#8b5cf6';
    case 'generated_by_run':
    case 'processed_source':
      return '#ef4444';
    case 'derived_from_page':
      return '#14b8a6';
    case 'co_mentions':
      return '#94a3b8';
  }
}

function summarizeAggregateBucket(type: KnowledgeGraphNodeType, bucket: KnowledgeGraphVisualNode[]) {
  const normalizeLabel = (node: KnowledgeGraphVisualNode) => {
    if (type === 'source') {
      return node.label.replace(/\.[a-z0-9]+$/i, '');
    }

    if (type === 'run') {
      const runType = typeof node.raw.metadata.runType === 'string' ? node.raw.metadata.runType : '';
      const status = typeof node.raw.metadata.status === 'string' ? node.raw.metadata.status : '';
      return [runType, status].filter(Boolean).join(' · ') || node.label;
    }

    return node.label;
  };

  const labels = Array.from(new Set(
    bucket
      .map(normalizeLabel)
      .map((label) => label.trim())
      .filter(Boolean)
  ));

  const visibleLabels = labels.slice(0, 3);
  if (visibleLabels.length === 0) {
    return nodeTypeLabels[type];
  }

  const remainder = labels.length - visibleLabels.length;
  return remainder > 0
    ? `${visibleLabels.join(' • ')} +${remainder}`
    : visibleLabels.join(' • ');
}

function buildPageOverviewBuckets(nodes: KnowledgeGraphVisualNode[]): OverviewBucket[] {
  const grouped = new Map<string, { order: number; label: string; nodes: KnowledgeGraphVisualNode[] }>();

  const getBucketDefinition = (node: KnowledgeGraphVisualNode) => {
    const path = typeof node.raw.metadata.path === 'string' ? node.raw.metadata.path.toLowerCase() : '';

    if (path.startsWith('sources/')) {
      return { key: 'page-folder:sources', label: 'Source Pages', order: 1 };
    }

    if (path.startsWith('topics/')) {
      return { key: 'page-folder:topics', label: 'Topic Pages', order: 2 };
    }

    if (path.startsWith('entities/')) {
      return { key: 'page-folder:entities', label: 'Entity Pages', order: 3 };
    }

    if (path.startsWith('syntheses/')) {
      return { key: 'page-folder:syntheses', label: 'Synthesis Pages', order: 4 };
    }

    return { key: 'page-folder:core', label: 'Core Pages', order: 0 };
  };

  nodes.forEach((node) => {
    const bucket = getBucketDefinition(node);
    const existing = grouped.get(bucket.key);

    if (existing) {
      existing.nodes.push(node);
      return;
    }

    grouped.set(bucket.key, {
      order: bucket.order,
      label: bucket.label,
      nodes: [node],
    });
  });

  return Array.from(grouped.entries())
    .sort(([, left], [, right]) => left.order - right.order || left.label.localeCompare(right.label))
    .map(([key, bucket]) => ({
      key,
      type: 'page',
      label: bucket.label,
      nodes: bucket.nodes.sort((left, right) => left.label.localeCompare(right.label)),
    }));
}

function useElementSize<T extends HTMLElement>(ref: React.RefObject<T | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const nextWidth = Math.round(entry.contentRect.width);
      const nextHeight = Math.round(entry.contentRect.height);
      setSize((current) => (
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      ));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

function buildOverviewGraph(
  nodes: KnowledgeGraphVisualNode[],
  edges: KnowledgeGraphEdge[],
  canvasWidth: number,
): { nodes: RenderGraphNode[]; edges: RenderGraphEdge[] } {
  const aggregateBucketSize = 6;
  const bucketWidth = 252;
  const laneGap = canvasWidth >= 1600 ? 40 : canvasWidth >= 1280 ? 32 : 24;
  const groupGap = canvasWidth >= 1600 ? 96 : canvasWidth >= 1280 ? 80 : 64;
  const rowGap = 94;
  const maxGraphWidth = Math.max(1400, Math.min(canvasWidth * 2.2, canvasWidth / 0.42));
  const renderedNodes: RenderGraphNode[] = [];
  const nodeIdToAggregateId = new Map<string, string>();

  const groupedBuckets = nodeTypeOrder
    .map((type) => {
      const group = nodes
        .filter((node) => node.type === type)
        .sort((a, b) => a.position.y - b.position.y);

      if (type === 'page') {
        return { type, buckets: buildPageOverviewBuckets(group) };
      }

      const buckets: OverviewBucket[] = [];
      for (let index = 0; index < group.length; index += aggregateBucketSize) {
        buckets.push({
          key: `${type}:${index / aggregateBucketSize}`,
          type,
          nodes: group.slice(index, index + aggregateBucketSize),
        });
      }

      return { type, buckets };
    })
    .filter((group) => group.buckets.length > 0);

  const laneCountByType = new Map<KnowledgeGraphNodeType, number>(
    groupedBuckets.map(({ type }) => [type, 1])
  );

  const groupWidthForLanes = (laneCount: number) => (
    laneCount * bucketWidth + Math.max(0, laneCount - 1) * laneGap
  );

  const totalGraphWidth = (draftLaneCountByType = laneCountByType) => (
    groupedBuckets.reduce((total, { type }) => (
      total + groupWidthForLanes(draftLaneCountByType.get(type) ?? 1)
    ), 0) + Math.max(0, groupedBuckets.length - 1) * groupGap
  );

  while (true) {
    let bestCandidate: { type: KnowledgeGraphNodeType; score: number } | null = null;

    groupedBuckets.forEach(({ type, buckets }) => {
      const currentLaneCount = laneCountByType.get(type) ?? 1;
      if (currentLaneCount >= buckets.length) return;

      const currentRows = Math.ceil(buckets.length / currentLaneCount);
      const nextRows = Math.ceil(buckets.length / (currentLaneCount + 1));
      const rowsSaved = currentRows - nextRows;
      if (rowsSaved <= 0) return;

      const nextLaneCountByType = new Map(laneCountByType);
      nextLaneCountByType.set(type, currentLaneCount + 1);
      if (totalGraphWidth(nextLaneCountByType) > maxGraphWidth) return;

      const score = rowsSaved * 100 + currentRows;
      if (!bestCandidate || score > bestCandidate.score) {
        bestCandidate = { type, score };
      }
    });

    if (!bestCandidate) break;
    laneCountByType.set(bestCandidate.type, (laneCountByType.get(bestCandidate.type) ?? 1) + 1);
  }

  let currentX = -totalGraphWidth() / 2;

  groupedBuckets.forEach(({ type, buckets }) => {
    const laneCount = laneCountByType.get(type) ?? 1;
    const rowsPerLane = Math.ceil(buckets.length / laneCount);
    const startY = -((rowsPerLane - 1) * rowGap) / 2;
    const groupWidth = groupWidthForLanes(laneCount);

    buckets.forEach((bucket, bucketIndex) => {
      if (bucket.nodes.length === 0) return;

      const laneIndex = Math.floor(bucketIndex / rowsPerLane);
      const rowIndex = bucketIndex % rowsPerLane;
      const x = currentX + laneIndex * (bucketWidth + laneGap);
      const y = startY + rowIndex * rowGap;

      if (bucket.nodes.length === 1 && !bucket.label) {
        const onlyNode = bucket.nodes[0];
        nodeIdToAggregateId.set(onlyNode.id, onlyNode.id);
        renderedNodes.push({
          id: onlyNode.id,
          label: onlyNode.label,
          metaLabel: undefined,
          type: onlyNode.type,
          position: { x, y },
          raw: onlyNode.raw,
          memberNodeIds: [onlyNode.id],
          count: 1,
          isAggregate: false,
        });
        return;
      }

      const aggregateId = `aggregate:${type}:${bucket.key}`;
      bucket.nodes.forEach((node) => nodeIdToAggregateId.set(node.id, aggregateId));

      renderedNodes.push({
        id: aggregateId,
        label: bucket.label ?? summarizeAggregateBucket(type, bucket.nodes),
        metaLabel: `${bucket.nodes.length} ${bucket.nodes.length === 1 ? nodeTypeLabels[type].slice(0, -1) : nodeTypeLabels[type]}`,
        type,
        position: { x, y },
        raw: null,
        memberNodeIds: bucket.nodes.map((node) => node.id),
        count: bucket.nodes.length,
        isAggregate: true,
      });
    });

    currentX += groupWidth + groupGap;
  });

  const edgeMap = new Map<string, RenderGraphEdge>();

  edges.forEach((edge) => {
    const sourceId = nodeIdToAggregateId.get(edge.from_node_id) ?? edge.from_node_id;
    const targetId = nodeIdToAggregateId.get(edge.to_node_id) ?? edge.to_node_id;

    if (sourceId === targetId) {
      return;
    }

    const key = `${sourceId}:${targetId}:${edge.edge_type}`;
    const existing = edgeMap.get(key);

    if (existing) {
      existing.weight += edge.weight;
      existing.count = (existing.count ?? 1) + 1;
      return;
    }

    edgeMap.set(key, {
      ...edge,
      id: key,
      from_node_id: sourceId,
      to_node_id: targetId,
      isAggregate: sourceId.startsWith('aggregate:') || targetId.startsWith('aggregate:'),
      count: 1,
    });
  });

  return {
    nodes: renderedNodes,
    edges: Array.from(edgeMap.values()),
  };
}

export default function KnowledgeGraphView({ knowledgeBaseId, refreshKey, fullscreenHostRef, onOpenVaultPage }: KnowledgeGraphViewProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasSize = useElementSize(canvasRef);
  const { desktopConfig, isLoaded: desktopConfigLoaded, saveDesktopConfig } = useUserDesktopConfig();
  const { nodes, edges, visualNodes, loading, defaultFilters } = useKnowledgeGraph(
    knowledgeBaseId,
    refreshKey,
    canvasSize.width,
    canvasSize.height,
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [search, setSearch] = useState(defaultFilters.search);
  const [enabledNodeTypes, setEnabledNodeTypes] = useState<KnowledgeGraphNodeType[]>(defaultFilters.nodeTypes);
  const [enabledEdgeTypes, setEnabledEdgeTypes] = useState<KnowledgeGraphEdgeType[]>(defaultFilters.edgeTypes as KnowledgeGraphEdgeType[]);
  const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewportZoom, setViewportZoom] = useState(1);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [hasHydratedViewportState, setHasHydratedViewportState] = useState(false);
  const [persistedViewportByKnowledgeBaseId, setPersistedViewportByKnowledgeBaseId] = useState<Record<string, GraphViewportState>>({});
  const restoredViewportKnowledgeBaseIdRef = useRef<string | null>(null);

  const persistViewportState = useCallback((viewport: GraphViewportState) => {
    if (!knowledgeBaseId) return;

    const nextViewport = {
      x: Number(viewport.x.toFixed(2)),
      y: Number(viewport.y.toFixed(2)),
      zoom: Number(viewport.zoom.toFixed(3)),
    };

    setPersistedViewportByKnowledgeBaseId((current) => {
      const existingViewport = current[knowledgeBaseId];
      if (
        existingViewport &&
        existingViewport.x === nextViewport.x &&
        existingViewport.y === nextViewport.y &&
        existingViewport.zoom === nextViewport.zoom
      ) {
        return current;
      }

      return {
        ...current,
        [knowledgeBaseId]: nextViewport,
      };
    });
  }, [knowledgeBaseId]);

  const persistCurrentViewport = useCallback(() => {
    const viewport = reactFlowInstance?.getViewport();
    if (!viewport) return;
    persistViewportState(viewport);
  }, [persistViewportState, reactFlowInstance]);

  useEffect(() => {
    if (hasHydratedViewportState || !desktopConfigLoaded) {
      return;
    }

    const localViewportMap = typeof window === 'undefined'
      ? {}
      : parseGraphViewportMap(window.localStorage.getItem(GRAPH_VIEWPORT_STORAGE_KEY));
    const remoteViewportMap = parseGraphViewportMap(desktopConfig[GRAPH_VIEWPORT_CONFIG_KEY]);
    const nextViewportMap = Object.keys(remoteViewportMap).length > 0 ? remoteViewportMap : localViewportMap;

    setPersistedViewportByKnowledgeBaseId(nextViewportMap);
    setHasHydratedViewportState(true);

    if (Object.keys(remoteViewportMap).length === 0 && Object.keys(localViewportMap).length > 0) {
      void saveDesktopConfig(GRAPH_VIEWPORT_CONFIG_KEY, localViewportMap);
    }
  }, [desktopConfig, desktopConfigLoaded, hasHydratedViewportState, saveDesktopConfig]);

  useEffect(() => {
    if (!hasHydratedViewportState) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(GRAPH_VIEWPORT_STORAGE_KEY, JSON.stringify(persistedViewportByKnowledgeBaseId));
    }

    if (desktopConfigLoaded) {
      void saveDesktopConfig(GRAPH_VIEWPORT_CONFIG_KEY, persistedViewportByKnowledgeBaseId);
    }
  }, [desktopConfigLoaded, hasHydratedViewportState, persistedViewportByKnowledgeBaseId, saveDesktopConfig]);

  const persistedViewport = useMemo(
    () => (knowledgeBaseId ? persistedViewportByKnowledgeBaseId[knowledgeBaseId] ?? null : null),
    [knowledgeBaseId, persistedViewportByKnowledgeBaseId]
  );

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
        setSelectedNodeId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    if (!selectedNodeId) {
      setIsInspectorCollapsed(isFullscreen);
      return;
    }

    setIsInspectorCollapsed(false);
  }, [isFullscreen, selectedNodeId]);

  const detailLevel = useMemo<DetailLevel>(() => {
    if (viewportZoom < 0.9) return 'overview';
    if (viewportZoom < 1.28) return 'compact';
    return 'full';
  }, [viewportZoom]);

  const filteredNodes = useMemo(() => {
    const query = search.trim().toLowerCase();

    return visualNodes.filter((node) => {
      if (!enabledNodeTypes.includes(node.type)) return false;
      if (!query) return true;

      const haystack = [
        node.label,
        node.raw.summary ?? '',
        node.raw.content_excerpt ?? '',
        String(node.raw.metadata.path ?? ''),
      ].join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [enabledNodeTypes, search, visualNodes]);

  const allowedNodeIds = useMemo(() => new Set(filteredNodes.map((node) => node.id)), [filteredNodes]);

  const filteredEdges = useMemo(
    () => edges.filter((edge) => enabledEdgeTypes.includes(edge.edge_type) && allowedNodeIds.has(edge.from_node_id) && allowedNodeIds.has(edge.to_node_id)),
    [allowedNodeIds, edges, enabledEdgeTypes]
  );

  const renderedGraph = useMemo(() => {
    if (detailLevel === 'overview') {
      return buildOverviewGraph(filteredNodes, filteredEdges, canvasSize.width);
    }

    return {
      nodes: filteredNodes.map((node) => ({
        id: node.id,
        label: node.label,
        metaLabel: undefined,
        type: node.type,
        position: node.position,
        raw: node.raw,
        memberNodeIds: [node.id],
        count: 1,
        isAggregate: false,
      } satisfies RenderGraphNode)),
      edges: filteredEdges.map((edge) => ({ ...edge, isAggregate: false } satisfies RenderGraphEdge)),
    };
  }, [canvasSize.width, detailLevel, filteredEdges, filteredNodes]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId]
  );

  const selectedConnections = useMemo(
    () => filteredEdges.filter((edge) => edge.from_node_id === selectedNodeId || edge.to_node_id === selectedNodeId),
    [filteredEdges, selectedNodeId]
  );

  const showInspector = !isInspectorCollapsed && (!isFullscreen || Boolean(selectedNode));

  useEffect(() => {
    if (!selectedNodeId) return;

    const selectedNodeStillVisible = renderedGraph.nodes.some((node) => node.id === selectedNodeId && !node.isAggregate);
    if (!selectedNodeStillVisible && detailLevel === 'overview') {
      setSelectedNodeId(null);
    }
  }, [detailLevel, renderedGraph.nodes, selectedNodeId]);

  useEffect(() => {
    if (
      !reactFlowInstance ||
      !hasHydratedViewportState ||
      renderedGraph.nodes.length === 0 ||
      canvasSize.width === 0 ||
      canvasSize.height === 0
    ) {
      return;
    }

    const fitPadding = detailLevel === 'overview'
      ? (isFullscreen ? 0.015 : 0.03)
      : isFullscreen
        ? 0.06
        : 0.1;
    const fitMinZoom = detailLevel === 'overview' ? 0.4 : 0.15;
    const currentKnowledgeBaseId = knowledgeBaseId ?? null;

    const frame = window.requestAnimationFrame(() => {
      if (
        currentKnowledgeBaseId &&
        persistedViewport &&
        restoredViewportKnowledgeBaseIdRef.current !== currentKnowledgeBaseId
      ) {
        reactFlowInstance.setViewport(persistedViewport, { duration: 0 });
        setViewportZoom(persistedViewport.zoom);
        restoredViewportKnowledgeBaseIdRef.current = currentKnowledgeBaseId;
        return;
      }

      if (!persistedViewport) {
        reactFlowInstance.fitView({
          padding: fitPadding,
          duration: 300,
          includeHiddenNodes: false,
          minZoom: fitMinZoom,
        });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    canvasSize.height,
    canvasSize.width,
    detailLevel,
    hasHydratedViewportState,
    isFullscreen,
    knowledgeBaseId,
    persistedViewport,
    reactFlowInstance,
    renderedGraph.nodes.length,
  ]);

  const rfNodes = useMemo<Node[]>(() => {
    return renderedGraph.nodes.map((node) => {
      const Icon = iconForType(node.type);
      const color = colorForType(node.type);
      const backgroundColor = backgroundColorForType(node.type);
      const isOverview = detailLevel === 'overview';
      const isCompact = detailLevel === 'compact';
      const width = node.isAggregate
        ? 252
        : isOverview
          ? 54
          : isCompact
            ? 180
            : 220;

      return {
        id: node.id,
        position: node.position,
        draggable: false,
        selectable: true,
        data: {
          isAggregate: node.isAggregate,
          memberNodeIds: node.memberNodeIds,
          label: (
            <div className={cn('flex items-center', node.isAggregate || isOverview ? 'justify-center gap-3.5' : 'gap-2')}>
              <Icon className={cn(node.isAggregate ? 'h-[22px] w-[22px]' : 'h-4 w-4')} />
              {node.isAggregate ? (
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-[13px] font-semibold leading-4">{node.label}</div>
                  {node.metaLabel ? (
                    <div className="mt-1 truncate text-[11px] font-medium leading-none text-slate-500">{node.metaLabel}</div>
                  ) : null}
                </div>
              ) : isOverview ? null : (
                <span className={cn('truncate font-medium', isCompact ? 'max-w-[132px] text-sm' : 'max-w-[180px] text-sm')}>
                  {node.label}
                </span>
              )}
            </div>
          ),
        },
        style: {
          width,
          minHeight: node.isAggregate ? 70 : isOverview ? 44 : undefined,
          borderRadius: 14,
          border: `1px solid ${color}`,
          background: backgroundColor,
          color: '#0f172a',
          padding: node.isAggregate ? 14 : isOverview ? 10 : isCompact ? 10 : 12,
          boxShadow: '0 10px 25px rgba(15, 23, 42, 0.08)',
          opacity: detailLevel === 'overview' && !node.isAggregate ? 0.9 : 1,
        },

      };
    });
  }, [detailLevel, renderedGraph.nodes]);

  const rfEdges = useMemo<Edge[]>(() => {
    return renderedGraph.edges.map((edge) => ({
      id: edge.id,
      source: edge.from_node_id,
      target: edge.to_node_id,
      label: detailLevel === 'full' ? edgeTypeLabels[edge.edge_type] : undefined,
      animated: detailLevel === 'full' && (edge.edge_type === 'mentions' || edge.edge_type === 'generated_by_run' || edge.edge_type === 'processed_source'),
      style: {
        stroke: edgeColor(edge.edge_type),
        strokeWidth: detailLevel === 'overview'
          ? Math.min(2.8, 1 + Math.log2((edge.count ?? 1) + 1))
          : edge.edge_type === 'wiki_link'
            ? 1.8
            : 1.4,
        opacity: detailLevel === 'overview' ? 0.42 : detailLevel === 'compact' ? 0.55 : 0.72,
      },
      labelStyle: { fill: '#64748b', fontSize: 10 },
    }));
  }, [detailLevel, renderedGraph.edges]);

  const toggleNodeType = (type: KnowledgeGraphNodeType) => {
    setEnabledNodeTypes((current) => (
      current.includes(type) ? current.filter((item) => item !== type) : [...current, type]
    ));
  };

  const toggleEdgeType = (type: KnowledgeGraphEdgeType) => {
    setEnabledEdgeTypes((current) => (
      current.includes(type) ? current.filter((item) => item !== type) : [...current, type]
    ));
  };

  const openVaultReference = (target: { pageId?: string | null; path?: string | null }) => {
    persistCurrentViewport();
    onOpenVaultPage?.(target);
  };

  const renderInspector = (floating: boolean) => (
    <Card className={cn('flex h-full min-h-0 flex-col overflow-hidden', floating && 'shadow-xl')}>
      <div className="flex h-full min-h-0 flex-col p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold">Inspector</h3>
            <p className="text-xs text-muted-foreground">Select a graph node to inspect its metadata and relationships.</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{selectedNode ? nodeTypeLabels[selectedNode.node_type] : 'Idle'}</Badge>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => {
                setIsInspectorCollapsed(true);
                setSelectedNodeId(null);
              }}
              title="Collapse inspector"
              aria-label="Collapse inspector"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Separator className="my-4" />

        {!selectedNode ? (
          <p className="text-sm text-muted-foreground">Nothing selected yet.</p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {React.createElement(iconForType(selectedNode.node_type), { className: 'h-4 w-4' })}
                <span className="font-medium">{selectedNode.label}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{nodeTypeLabels[selectedNode.node_type]}</Badge>
                {selectedNode.slug && <Badge variant="outline">{selectedNode.slug}</Badge>}
              </div>
            </div>

            {selectedNode.summary && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Summary</h4>
                <p className="mt-1 text-sm text-foreground/90">{selectedNode.summary}</p>
              </div>
            )}

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">References</h4>
              <div className="mt-2 space-y-3 text-sm text-muted-foreground">
                {selectedNode.metadata.path && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Path</div>
                    {onOpenVaultPage ? (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-left text-sm font-normal text-primary"
                        onClick={() => openVaultReference({
                          pageId: selectedNode.vault_page_id,
                          path: String(selectedNode.metadata.path),
                        })}
                      >
                        {String(selectedNode.metadata.path)}
                      </Button>
                    ) : (
                      <div>{String(selectedNode.metadata.path)}</div>
                    )}
                  </div>
                )}
                {selectedNode.vault_page_id && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Vault page</div>
                    {onOpenVaultPage ? (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-left text-sm font-normal text-primary"
                        onClick={() => openVaultReference({ pageId: selectedNode.vault_page_id })}
                      >
                        {selectedNode.vault_page_id}
                      </Button>
                    ) : (
                      <div>{selectedNode.vault_page_id}</div>
                    )}
                  </div>
                )}
                {selectedNode.source_document_id && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Source document</div>
                    <div>{selectedNode.source_document_id}</div>
                  </div>
                )}
                {selectedNode.vault_run_id && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Run</div>
                    <div>{selectedNode.vault_run_id}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connected edges</h4>
              <ScrollArea className="mt-2 h-full rounded-md border">
                <div className="space-y-2 p-3">
                  {selectedConnections.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No visible edges for this node.</p>
                  ) : (
                    selectedConnections.map((edge) => (
                      <div key={edge.id} className="rounded-md border p-2">
                        <div className="text-sm font-medium">{edgeTypeLabels[edge.edge_type]}</div>
                        <div className="text-xs text-muted-foreground">Weight {edge.weight.toFixed(2)}</div>
                        {edge.evidence && <div className="mt-1 text-xs text-foreground/80">{edge.evidence}</div>}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const renderGraphShell = (fullscreenMode: boolean) => {
    const showDockedInspector = showInspector && !fullscreenMode;
    const showFloatingInspector = showInspector && fullscreenMode;

    return (
      <div className={cn('relative h-full min-h-0 overflow-hidden bg-background', fullscreenMode && 'absolute inset-0 z-[100]')}>
        <div className={cn('grid h-full min-h-0 gap-4 overflow-hidden', showDockedInspector ? 'lg:grid-cols-[minmax(0,1fr)_24rem]' : 'grid-cols-1')}>
          <div className={cn('flex h-full min-h-0 flex-col overflow-hidden rounded-xl border bg-card', fullscreenMode && 'rounded-none border-0')}>
            {!fullscreenMode && (
              <div className="border-b p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">Nodes {renderedGraph.nodes.length}</Badge>
                    <Badge variant="secondary">Edges {renderedGraph.edges.length}</Badge>
                    <Badge variant="outline">{detailLevel === 'full' ? 'Detailed view' : detailLevel === 'compact' ? 'Compact view' : 'Overview clusters'}</Badge>
                    {loading && (
                      <Badge variant="outline" className="gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Syncing graph
                      </Badge>
                    )}
                    <div className="ml-auto flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsInspectorCollapsed((current) => !current)}
                      >
                        {isInspectorCollapsed ? <PanelRightOpen className="mr-2 h-4 w-4" /> : <PanelRightClose className="mr-2 h-4 w-4" />}
                        {isInspectorCollapsed ? '' : ''}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div className="relative w-full xl:max-w-sm">
                      <ScanSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search graph nodes"
                        className="pl-9"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(nodeTypeLabels) as KnowledgeGraphNodeType[]).map((type) => (
                        <Button
                          key={type}
                          size="sm"
                          variant={enabledNodeTypes.includes(type) ? 'default' : 'outline'}
                          onClick={() => toggleNodeType(type)}
                        >
                          {nodeTypeLabels[type]}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(edgeTypeLabels) as KnowledgeGraphEdgeType[]).map((type) => (
                      <Button
                        key={type}
                        size="sm"
                        variant={enabledEdgeTypes.includes(type) ? 'secondary' : 'ghost'}
                        className="h-7"
                        onClick={() => toggleEdgeType(type)}
                      >
                        {edgeTypeLabels[type]}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {renderedGraph.nodes.length === 0 ? (
              <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-center text-muted-foreground">
                No graph nodes match the current filters yet.
              </div>
            ) : (
              <div ref={canvasRef} className="min-h-0 flex-1 bg-gradient-to-br from-background to-muted/30">
                <ReactFlowProvider>
                  <ReactFlow
                    nodes={rfNodes}
                    edges={rfEdges}
                    fitView
                    fitViewOptions={{
                      padding: detailLevel === 'overview'
                        ? (isFullscreen ? 0.015 : 0.03)
                        : isFullscreen
                          ? 0.06
                          : 0.1,
                      includeHiddenNodes: false,
                      minZoom: detailLevel === 'overview' ? 0.4 : 0.15,
                    }}
                    minZoom={detailLevel === 'overview' ? 0.4 : 0.15}
                    maxZoom={2.2}
                    onInit={setReactFlowInstance}
                    onMove={(_, viewport) => setViewportZoom(viewport.zoom)}
                    onMoveEnd={(_, viewport) => persistViewportState(viewport)}
                    onNodeClick={(_, node) => {
                      const isAggregate = Boolean(node.data?.isAggregate);

                      if (isAggregate) {
                        reactFlowInstance?.setCenter(node.position.x, node.position.y, {
                          zoom: 0.98,
                          duration: 250,
                        });
                        return;
                      }

                      setSelectedNodeId(node.id);
                    }}
                    onPaneClick={() => setSelectedNodeId(null)}
                    nodesDraggable={false}
                    elementsSelectable
                    proOptions={{ hideAttribution: true }}
                  >
                    <MiniMap
                      pannable
                      zoomable
                      nodeColor={(node) => colorForType((renderedGraph.nodes.find((item) => item.id === node.id)?.type ?? 'page') as KnowledgeGraphNodeType)}
                    />
                    <Controls>
                      <ControlButton
                        onClick={() => {
                          setIsFullscreen((current) => !current);
                          if (!isFullscreen) {
                            setSelectedNodeId(null);
                          }
                        }}
                        aria-label={isFullscreen ? 'Exit full screen graph' : 'Enter full screen graph'}
                        title={isFullscreen ? 'Exit full screen' : 'Full screen graph'}
                        className="!bg-background !text-foreground hover:!bg-muted"
                        style={{ color: '#0f172a' }}
                      >
                        {isFullscreen ? <Minimize2 className="h-4 w-4" strokeWidth={2.25} /> : <Maximize2 className="h-4 w-4" strokeWidth={2.25} />}
                      </ControlButton>
                    </Controls>
                    <Background />
                  </ReactFlow>
                </ReactFlowProvider>
              </div>
            )}
          </div>

          {showDockedInspector && renderInspector(false)}
        </div>

        {showFloatingInspector && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-full max-w-sm p-3 opacity-100 transition-all duration-200 ease-out lg:w-[24rem]">
            <div className="pointer-events-auto h-full min-h-0 translate-x-0">
              {renderInspector(true)}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!knowledgeBaseId) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
        Open a knowledge base to view its graph.
      </div>
    );
  }

  if (isFullscreen && fullscreenHostRef?.current) {
    return (
      <>
        <div className="h-full min-h-0" />
        {createPortal(renderGraphShell(true), fullscreenHostRef.current)}
      </>
    );
  }

  return renderGraphShell(false);
}